import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DetailsContainer from "./utils/DetailsContainer";
import DetailsTable from "./utils/DetailsTable";

const AdjustmentDetails = ({
  canEdit = true,
  id,
  dataId,
  title,
  data = [],
  onAdd,
  totalAmount,
  onUpdateReference,
  currency,
  currencyRate,
  tabs = [],
  isCrossYA = false,
}) => {
  const theme = useTheme();
  const hasTabs = tabs.length > 1;
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id || "");

  useEffect(() => {
    if (!hasTabs) return;

    if (!tabs.some((tab) => tab.id === activeTabId)) {
      setActiveTabId(tabs[0]?.id || "");
    }
  }, [activeTabId, hasTabs, tabs]);

  useEffect(() => {
    if (!hasTabs) return undefined;

    const handleSelectTab = (event) => {
      const { containerId, tabId } = event.detail || {};

      if (containerId === id && tabs.some((tab) => tab.id === tabId)) {
        setActiveTabId(tabId);
      }
    };

    window.addEventListener(
      "main-tax-computation:select-adjustment-tab",
      handleSelectTab
    );

    return () => {
      window.removeEventListener(
        "main-tax-computation:select-adjustment-tab",
        handleSelectTab
      );
    };
  }, [hasTabs, id, tabs]);

  const activeTab = useMemo(() => {
    if (!hasTabs) return null;
    return tabs.find((tab) => tab.id === activeTabId) || tabs[0];
  }, [activeTabId, hasTabs, tabs]);

  if (!hasTabs) {
    return (
      <DetailsContainer
        id={id}
        title={title}
        onAdd={onAdd}
        totalAmount={totalAmount}
        rows={data.length}
        isCrossYA={isCrossYA}
      >
        <DetailsTable
          currency={currency}
          currencyRate={currencyRate}
          dataId={dataId}
          data={data}
          noRowsHint='No adjustment details available.'
          maxHeight={600}
          onUpdateReference={onUpdateReference}
          canEdit={canEdit}
          isCrossYA={isCrossYA}
        />
      </DetailsContainer>
    );
  }

  return (
    <DetailsContainer
      id={id}
      title={title}
      onAdd={onAdd}
      totalAmount={totalAmount}
      rows={activeTab?.data?.length || 0}
    >
      <Box
        sx={{
          px: 3,
          pt: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tabs
          value={activeTab?.id || false}
          onChange={(_, value) => setActiveTabId(value)}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            minHeight: 44,
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: 3,
            },
            "& .MuiTab-root": {
              minHeight: 44,
              px: 2,
              py: 1,
              mr: 1,
              borderRadius: "8px 8px 0 0",
              textTransform: "none",
              color: "text.secondary",
              alignItems: "flex-start",
              "&.Mui-selected": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: "primary.main",
              },
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography
                    component='span'
                    variant='body2'
                    sx={{ fontWeight: 600 }}
                  >
                    {tab.label}
                  </Typography>
                  <Box
                    component='span'
                    sx={{
                      minWidth: 24,
                      height: 22,
                      px: 0.75,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.text.primary, 0.08),
                      color: "text.secondary",
                    }}
                  >
                    {tab.data?.length || 0}
                  </Box>
                </Stack>
              }
            />
          ))}
        </Tabs>
      </Box>
      <DetailsTable
        currency={currency}
        currencyRate={currencyRate}
        dataId={activeTab?.dataId}
        data={activeTab?.data || []}
        noRowsHint='No adjustment details available.'
        maxHeight={600}
        onUpdateReference={onUpdateReference}
        canEdit={canEdit}
        isCrossYA={isCrossYA}
      />
    </DetailsContainer>
  );
};

export default AdjustmentDetails;
